/**
 * Drive a chunked recording session from the browser.
 *
 * The old flow did every byte of work after the meeting ended: hold 30 minutes of
 * audio in a JS array, build one File, upload it, wait for one huge transcription,
 * then summarise. This hook moves upload and transcription *into* the meeting, so
 * what is left at the end is a stitch and a summary.
 *
 * It degrades rather than fails. If a session cannot be opened — the worker is
 * down, the network is out, credits are exhausted — `unavailable` is set and the
 * caller falls back to the whole-file path, which still works exactly as before.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import meetlyApi, { type MeetlyChunkUpload } from '../api/meetly';
import MeetlyChunkQueue, { type ChunkQueueState } from '../utils/meetlyChunkQueue';
import type { RecordedSegment } from '../utils/meetlySegmentedRecorder';

/** Survives a page refresh or a desktop window reopening mid-meeting. */
const ACTIVE_SESSION_KEY = 'meetly.activeSession';

export interface ActiveSessionRecord {
  sessionId: string;
  meetingId: string;
  startedAt: number;
  captureMode: string;
  language: string;
  summaryTemplate: string;
}

export const readActiveSession = (): ActiveSessionRecord | null => {
  try {
    const raw = window.localStorage.getItem(ACTIVE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveSessionRecord;
    return parsed?.sessionId && parsed?.meetingId ? parsed : null;
  } catch {
    // Private windows and blocked site data both throw here. A recorder that
    // cannot remember its session is still a working recorder.
    return null;
  }
};

const writeActiveSession = (record: ActiveSessionRecord | null) => {
  try {
    if (record) window.localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(record));
    else window.localStorage.removeItem(ACTIVE_SESSION_KEY);
  } catch {
    // See readActiveSession.
  }
};

export const clearActiveSession = () => writeActiveSession(null);

export interface ChunkedSessionState extends ChunkQueueState {
  sessionId: string | null;
  meetingId: string | null;
  /** True when chunking could not be used and the caller should buffer instead. */
  unavailable: boolean;
  unavailableReason: string | null;
}

const INITIAL: ChunkedSessionState = {
  sessionId: null,
  meetingId: null,
  unavailable: false,
  unavailableReason: null,
  uploaded: 0,
  pending: 0,
  failed: 0,
  retrying: false,
  lastError: null,
};

interface OpenOptions {
  captureMode: string;
  mimeType: string;
  language?: string;
  summaryTemplate?: string;
  /** Rejoin an existing meeting rather than opening a new one. */
  meetingId?: string;
}

export function useMeetlyChunkedSession() {
  const [state, setState] = useState<ChunkedSessionState>(INITIAL);
  const queueRef = useRef<MeetlyChunkQueue | null>(null);
  const sessionRef = useRef<{ sessionId: string; meetingId: string } | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queueRef.current?.dispose();
      queueRef.current = null;
    };
  }, []);

  const patch = useCallback((next: Partial<ChunkedSessionState>) => {
    if (!mountedRef.current) return;
    setState(current => ({ ...current, ...next }));
  }, []);

  const open = useCallback(async (options: OpenOptions): Promise<ActiveSessionRecord | null> => {
    setState(INITIAL);
    try {
      const response = await meetlyApi.createSession({
        meeting_id: options.meetingId,
        language: options.language || 'en',
        summary_template: options.summaryTemplate || 'project_sync',
        mime_type: options.mimeType,
        capture_mode: options.captureMode,
      });
      const { session_id: sessionId, meeting_id: meetingId } = response.data;
      sessionRef.current = { sessionId, meetingId };

      const upload = async (chunk: MeetlyChunkUpload) => {
        const ack = await meetlyApi.uploadChunk(sessionId, chunk);
        return { duplicate: Boolean(ack.data?.duplicate) };
      };

      queueRef.current?.dispose();
      queueRef.current = new MeetlyChunkQueue({
        upload,
        onProgress: queueState => patch(queueState),
        onStalled: queueState => patch(queueState),
      });

      const record: ActiveSessionRecord = {
        sessionId,
        meetingId,
        startedAt: Date.now(),
        captureMode: options.captureMode,
        language: options.language || 'en',
        summaryTemplate: options.summaryTemplate || 'project_sync',
      };
      writeActiveSession(record);
      patch({ sessionId, meetingId, unavailable: false, unavailableReason: null });
      return record;
    } catch (error) {
      // Credits exhausted is the one case the caller must surface rather than
      // silently fall back to: the whole-file path would fail the same way, after
      // the user has already spoken for thirty minutes.
      const response = (error as {
        response?: { status?: number; data?: { detail?: { workspace?: { remaining?: number } | null } } };
      })?.response;
      const status = response?.status;
      // A 429 carries the whole balance, so it can say WHICH allowance ran out.
      // Naming the user's own when it was the workspace's sends them to their
      // admin over a limit only Teamlio can raise.
      const workspaceDry = (response?.data?.detail?.workspace?.remaining ?? 1) <= 0;
      patch({
        unavailable: true,
        unavailableReason: status === 429
          ? workspaceDry
            ? 'Your workspace has used all of its Meetly meetings for this month.'
            : 'You have used all of your Meetly meetings for this month.'
          : 'Live processing is unavailable, so this meeting will be processed when you finish.',
      });
      return null;
    }
  }, [patch]);

  const push = useCallback((segment: RecordedSegment) => {
    queueRef.current?.enqueue(segment);
  }, []);

  /**
   * End the meeting: get every outstanding chunk up, then tell the server to
   * assemble. Returns the meeting id to navigate to, or null when chunking was
   * never available.
   */
  const finish = useCallback(async (totalChunks: number): Promise<string | null> => {
    const session = sessionRef.current;
    const queue = queueRef.current;
    if (!session || !queue) return null;

    const settled = await queue.flush();
    patch(settled);

    try {
      await meetlyApi.finalizeSession(session.sessionId, { total_chunks: totalChunks });
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        // Chunks are missing server-side. One more flush, then force: the server
        // stitches what it has rather than stranding the meeting.
        await queue.flush();
        try {
          await meetlyApi.finalizeSession(session.sessionId, { force: true });
        } catch {
          patch({ lastError: 'Some audio is still being processed. We will keep retrying.' });
        }
      } else {
        patch({ lastError: 'Some audio is still being processed. We will keep retrying.' });
      }
    }
    clearActiveSession();
    return session.meetingId;
  }, [patch]);

  const abandon = useCallback(() => {
    queueRef.current?.dispose();
    queueRef.current = null;
    sessionRef.current = null;
    clearActiveSession();
    setState(INITIAL);
  }, []);

  return useMemo(
    () => ({ state, open, push, finish, abandon, isActive: Boolean(state.sessionId) }),
    [state, open, push, finish, abandon],
  );
}

export default useMeetlyChunkedSession;
